import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@heroui/react";
import { Paperclip } from "lucide-react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import type { QuotationItemInput } from "./api";

/** 明細と一緒にアップロードする図面 PDF。版はサーバー側で採番する。 */
export interface ItemAttachment {
  file: File;
}

/**
 * 見積明細の追加ダイアログ。
 *
 * ここで入れるのは**行を起こす時点で分かっているもの**だけにしてある。残りは
 * 一覧のセルで直接編集できる（[[InlineEditCell]]）。
 *
 * 図番を入れると製作対象の行になり、**その都度新しい図面を 1 枚作る**。登録済みの
 * 図面から選ぶ形は採っていない（図番が重複してもよい）。図面を明細ごとに持たせて
 * いるので、図番・品名・材質は追加後に一覧のセルから直せる。図番を空にした行は
 * 運搬費・値引きなど製作対象でない行になる。
 *
 * PDF はここから一緒に上げられる（図面を登録した流れでそのまま添付できるように
 * するため）。あとからの追加・差し替え・削除は [[DrawingRevisionsDialog]] で行う。
 */
export default function QuotationItemDialog({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  /**
   * 保存する。エラー文言を返すと閉じずに表示する。
   *
   * PDF の添付は明細の保存後にしかできない（図面 ID が要るため）。順序と
   * 失敗時の扱いを 1 箇所にまとめたいので、添付も呼び出し側に渡して
   * まとめて処理してもらう。
   */
  onSubmit: (
    input: QuotationItemInput,
    attachment: ItemAttachment | null,
  ) => Promise<string | null>;
}) {
  const [drawingNumber, setDrawingNumber] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("0");
  const [note, setNote] = useState("");
  // 依頼ファイルの行をそのまま写すときに要る項目。残りは一覧のセルで直せるので、
  // ここに置くのは「行を起こす時点で分かっているもの」だけにする。
  const [unit, setUnit] = useState("");
  const [requestedDeadline, setRequestedDeadline] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 開くたびに空に戻す（前回の入力を持ち越さない）。
  useEffect(() => {
    if (!isOpen) return;
    setDrawingNumber("");
    setItemName("");
    setQuantity("1");
    setUnitPrice("0");
    setNote("");
    setUnit("");
    setRequestedDeadline("");
    setFile(null);
    setError(null);
    setSaving(false);
  }, [isOpen]);

  const submit = async () => {
    const trimmedDrawing = drawingNumber.trim();
    const trimmedName = itemName.trim();
    if (!trimmedDrawing && !trimmedName) {
      setError("図番か品名のどちらかは必ず入力してください。");
      return;
    }

    const parsedQuantity = Number(quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
      setError("数量は 1 以上の整数で入力してください。");
      return;
    }
    if (!trimmedDrawing && !Number.isFinite(Number(unitPrice))) {
      setError("単価は数値で入力してください。");
      return;
    }
    if (file && !trimmedDrawing) {
      setError("PDF を添付するには図番を入力してください。");
      return;
    }

    const input: QuotationItemInput = {
      drawing_number: trimmedDrawing || null,
      item_name: trimmedName || null,
      quantity: parsedQuantity,
      note: note.trim() || null,
      unit: unit.trim() || null,
      requested_deadline: requestedDeadline.trim() || null,
    };
    // 図面行の単価は工程別見積の合算なので送らない（送っても無視される）。
    if (!trimmedDrawing) input.unit_price = unitPrice.trim() || "0";

    setSaving(true);
    const message = await onSubmit(input, file ? { file } : null);
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>明細の追加</span>
          <span className="text-small font-normal text-default-500">
            図番を入れると製作対象の行になり、図面を 1
            枚新しく登録します（同じ図番の行が何行あってもかまいません）。図面行の単価は、追加したあとに工程ごとの見積を入れて決めます。運搬費・値引きなどは図番を空にして品名だけ入れてください（値引きは単価をマイナスに）。
          </span>
        </ModalHeader>

        <ModalBody>
          <Input
            label="図番"
            labelPlacement="outside"
            placeholder=" "
            aria-label="図番"
            variant="bordered"
            radius="sm"
            value={drawingNumber}
            description="追加したあとに一覧のセルから直せます。"
            onValueChange={(v) => {
              setDrawingNumber(v);
              setError(null);
            }}
          />

          <Input
            label="品名"
            labelPlacement="outside"
            placeholder=" "
            aria-label="品名"
            variant="bordered"
            radius="sm"
            value={itemName}
            description="図番を入れた行で省くと、図番を品名として入れておきます。"
            onValueChange={(v) => {
              setItemName(v);
              setError(null);
            }}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="数量"
              labelPlacement="outside"
              placeholder=" "
              aria-label="数量"
              type="number"
              min={1}
              step={1}
              variant="bordered"
              radius="sm"
              value={quantity}
              onValueChange={(v) => {
                setQuantity(v);
                setError(null);
              }}
            />
            {/* 図面行の単価は工程別見積の合算なので、ここでは入力させない。 */}
            {drawingNumber.trim() ? (
              <Input
                label="単価"
                labelPlacement="outside"
                placeholder=" "
                aria-label="単価"
                variant="bordered"
                radius="sm"
                isDisabled
                value=""
                description="図面の単価は、明細を追加したあとに工程ごとの見積を入れて決めます。"
              />
            ) : (
              <Input
                label="単価"
                labelPlacement="outside"
                placeholder=" "
                aria-label="単価"
                type="number"
                step="0.01"
                variant="bordered"
                radius="sm"
                value={unitPrice}
                description="値引きはマイナスで入力します。"
                onValueChange={(v) => {
                  setUnitPrice(v);
                  setError(null);
                }}
              />
            )}
          </div>

          {/* 依頼ファイルの行をそのまま写すための項目。 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="単位"
              labelPlacement="outside"
              placeholder=" "
              aria-label="単位"
              variant="bordered"
              radius="sm"
              value={unit}
              description="PC / 式 など。"
              onValueChange={setUnit}
            />
            <Input
              label="希望納期"
              labelPlacement="outside"
              placeholder=" "
              aria-label="希望納期"
              type="date"
              variant="bordered"
              radius="sm"
              value={requestedDeadline}
              description="客先の希望です。"
              onValueChange={setRequestedDeadline}
            />
          </div>

          <Textarea
            label="摘要"
            labelPlacement="outside"
            placeholder=" "
            aria-label="摘要"
            variant="bordered"
            radius="sm"
            minRows={2}
            value={note}
            onValueChange={setNote}
          />

          {/* 図面を登録した流れでそのまま添付できるようにする。あとからの
              追加・差し替え・削除は明細の見積画面から行う。 */}
          <div className="rounded-md border border-default-200 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-default-700">
              <Paperclip size={16} />
              図面 PDF（任意）
            </div>
            <div className="flex flex-col gap-3">
              <FileDropZone
                label="図面PDF"
                accept=".pdf"
                showLabel={false}
                file={file}
                onSelect={(selected) => {
                  setFile(selected);
                  setError(null);
                }}
              />
            </div>
            <p className="mt-2 text-tiny text-default-500">
              図番を入れた行にだけ添付できます。版は自動で採番されます。差し替えは明細を開いて「図面を更新」から行います。
            </p>
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="item-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            追加
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
